import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import VerificationLabel from "@/components/VerificationLabel";

const STATUSES = ["draft", "awaiting", "verified", "needs_review", "archived"];

export default function AdminVerification() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("queue"); // queue | all
  const [filter, setFilter] = useState("");

  const load = () => {
    setLoading(true);
    const path = tab === "queue" ? "/admin/verifications/queue" : "/admin/verifications";
    api.get(path, { params: tab === "all" ? { page_size: 200 } : {} })
      .then((r) => setRows(r.data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [tab]);

  const markStatus = async (record_id, status) => {
    try {
      await api.patch(`/admin/verifications/${record_id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const filtered = filter ? rows.filter((r) => (r.entity_stub?.name || r.entity_stub?.title || "").toLowerCase().includes(filter.toLowerCase())) : rows;

  return (
    <div data-testid="admin-verification-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-ivy tracking-tight">Verification Queue</h1>
          <p className="text-sm text-stone mt-1">{rows.length} records</p>
        </div>
        <div className="flex border border-stone/40">
          <button onClick={() => setTab("queue")} className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] ${tab === "queue" ? "bg-ivy text-bone" : "text-ink hover:bg-stone/10"}`} data-testid="verif-tab-queue">Queue (not verified)</button>
          <button onClick={() => setTab("all")} className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] ${tab === "all" ? "bg-ivy text-bone" : "text-ink hover:bg-stone/10"}`} data-testid="verif-tab-all">All</button>
        </div>
      </div>

      <input
        type="search"
        placeholder="Filter by entity name…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="mb-4 border border-stone/40 bg-bone px-3 py-2 text-sm w-full max-w-sm focus:border-ivy outline-none"
        data-testid="admin-verif-filter"
      />

      {loading ? <p className="text-sm text-stone">Loading…</p> : filtered.length === 0 ? (
        <p className="text-sm text-stone italic">Queue is clear — all records are verified. 🎯</p>
      ) : (
        <div className="border border-stone/30 bg-bone" data-testid="admin-verif-table">
          <table className="w-full text-sm">
            <thead className="bg-bone-dim/40 text-[10px] uppercase tracking-[0.18em] text-brass">
              <tr>
                <th className="text-left px-4 py-2.5">Entity</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Next review</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.record_id} className="border-t border-stone/20 hover:bg-bone-dim/20">
                  <td className="px-4 py-2.5">
                    <div className="text-ink font-medium">{r.entity_stub?.name || r.entity_stub?.title || r.entity_id}</div>
                    <div className="text-[11px] text-stone">{r.entity_stub?.slug}</div>
                  </td>
                  <td className="px-4 py-2.5 text-ink-soft">{r.entity_type}</td>
                  <td className="px-4 py-2.5"><VerificationLabel status={r.status} /></td>
                  <td className="px-4 py-2.5 text-ink-soft text-xs">{(r.next_review_date || "—").slice(0, 10)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <select
                      value={r.status}
                      onChange={(e) => markStatus(r.record_id, e.target.value)}
                      className="border border-stone/40 bg-bone px-2 py-1 text-xs uppercase tracking-[0.14em]"
                      data-testid={`admin-verif-status-${r.record_id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
